/**
 * Hoe de kaart 'Externe data' bij een huishouden zijn gegevens indeelt.
 *
 * De bronhelpers (`selectEnergyLabelFacts`, `selectBagFacts`, `selectCbsMetrics`)
 * blijven antwoorden op 'wat geeft deze bron?'. Deze laag beantwoordt een andere
 * vraag: 'hoe tonen we dat aan iemand die met energiearmoede bezig is?'. Vandaar
 * dat de indeling hier zit en niet in de bronnen — die zijn per bron gegroepeerd
 * en dat moeten ze blijven.
 *
 * **De indeling volgt hoe energiearmoede gedefinieerd wordt**: een laag inkomen,
 * een hoge energierekening en een slechte woningkwaliteit. Dat zijn de eerste
 * drie secties, in die volgorde. Al het andere — demografie, huishoudens­
 * samenstelling, woningvoorraad — schuift naar één sectie onderaan.
 *
 * **Alleen de bovenste sectie staat open.** De kaart is daarmee zo kort mogelijk
 * bij het openen; wie meer wil, klapt uit. 'De bovenste' is de bovenste die er
 * écht staat: valt 'De woning' weg omdat er geen energielabel en geen
 * pandgegevens zijn, dan opent de eerstvolgende — anders zou de kaart helemaal
 * dicht opengaan.
 *
 * **Waarom inklappen en niet weglaten.** Het verzoek was 'alleen tonen wat over
 * energiearmoede gaat'. Deze kaart is alleen niet van één klant: wie huishoudens
 * gebruikt voor schulden of participatie heeft juist aan de demografische cijfers
 * iets. Weghalen kost hun gegevens, inklappen niet — en zo is er ook geen
 * instelling die iemand moet beheren.
 *
 * Verzoek BuurtKlusBedrijf, 2026-09-22.
 * AI-CONTEXT: See docs/features/adress-external-data.md
 */

/** @type {{woning: string, energie: string, inkomen: string, overig: string}} */
const SECTION_IDS = {
    woning: 'woning',
    energie: 'energie',
    inkomen: 'inkomen',
    overig: 'overig',
}

/**
 * De BAG-feiten die iets over de woningkwaliteit zeggen.
 *
 * Op `key` en niet op `label`: de kaart verdeelt feiten over secties, en een
 * hernoemd label zou een feit dan stil naar de verkeerde sectie verhuizen.
 * AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows
 */
const WONING_BAG_KEYS = ['bouwjaar', 'oppervlakte']

/** De CBS-groep die helemaal over energie gaat. */
const ENERGIE_GROUP = 'Energie'

/** De CBS-groep die helemaal over inkomen gaat. */
const INKOMEN_GROUP = 'Inkomen'

/**
 * Losse cijfers uit de groep 'Wonen' die bij inkomen horen.
 *
 * Energiearmoede concentreert zich in goedkope huur- en corporatiewoningen, dus
 * de WOZ-waarde en het aandeel huur/corporatie horen naast het inkomen en niet
 * bij de woningvoorraad.
 */
const INKOMEN_WONEN_KEYS = [
    'GemiddeldeWOZWaardeVanWoningen_39',
    'HuurwoningenTotaal_48',
    'InBezitWoningcorporatie_49',
]

/**
 * @typedef {object} ExternalFact
 * @property {string} label - Wat de lezer ziet staan
 * @property {*} value - De waarde zelf
 * @property {string} [unit] - Stuurt de opmaak in de kaart
 * @property {string} [key] - Stabiele sleutel, los van het label
 */

/**
 * @typedef {object} ExternalDataSection
 * @property {string} id - Sleutel uit SECTION_IDS
 * @property {string} title - Kop van de sectie
 * @property {Array<ExternalFact>} facts - Wat erin staat, in vaste volgorde
 * @property {boolean} defaultOpen - Of hij open staat bij het openen van de kaart
 */

const asArray = (value) => (Array.isArray(value) ? value : [])

/**
 * Deelt de externe gegevens van één huishouden in vier secties.
 *
 * Een sectie zonder inhoud valt weg: een lege kop is een belofte die niet
 * waargemaakt wordt.
 *
 * Een cijfer dat we niet kennen — CBS zet er bij een nieuwe jaartabel wel eens
 * iets bij — belandt bij 'Overige buurtcijfers'. Onderaan staan is beter dan
 * stil verdwijnen.
 *
 * @param {object} [input]
 * @param {Array<ExternalFact>} [input.energyLabelFacts] - Uit `selectEnergyLabelFacts`
 * @param {Array<ExternalFact>} [input.bagFacts] - Uit `selectBagFacts`
 * @param {Array<{group: string, metrics: Array<ExternalFact>}>} [input.cbsGroups] - Uit `selectCbsMetrics`
 * @returns {Array<ExternalDataSection>}
 */
const buildExternalDataSections = ({ energyLabelFacts, bagFacts, cbsGroups } = {}) => {
    const labelFacts = asArray(energyLabelFacts)
    const bag = asArray(bagFacts)
    const groups = asArray(cbsGroups)

    const bagIn = (wil) => bag.filter((fact) => WONING_BAG_KEYS.includes(fact.key) === wil)

    const metricsOf = (naam) => {
        const group = groups.find((g) => g && g.group === naam)
        return asArray(group && group.metrics)
    }

    const wonen = metricsOf('Wonen')

    const overigeGroepen = groups
        .filter((g) => g && g.group !== ENERGIE_GROUP && g.group !== INKOMEN_GROUP && g.group !== 'Wonen')
        .flatMap((g) => asArray(g.metrics))

    const secties = [
        {
            id: SECTION_IDS.woning,
            title: 'De woning',
            facts: [...labelFacts, ...bagIn(true)],
        },
        {
            id: SECTION_IDS.energie,
            title: 'Energie in de buurt',
            facts: metricsOf(ENERGIE_GROUP),
        },
        {
            id: SECTION_IDS.inkomen,
            title: 'Inkomen en bezit in de buurt',
            facts: [
                ...metricsOf(INKOMEN_GROUP),
                ...wonen.filter((m) => INKOMEN_WONEN_KEYS.includes(m.key)),
            ],
        },
        {
            id: SECTION_IDS.overig,
            title: 'Overige buurtcijfers',
            facts: [
                ...bagIn(false),
                ...overigeGroepen,
                ...wonen.filter((m) => !INKOMEN_WONEN_KEYS.includes(m.key)),
            ],
        },
    ]

    // Pas NA het weglaten van lege secties bepalen wat 'de bovenste' is.
    return secties
        .filter((sectie) => sectie.facts.length > 0)
        .map((sectie, index) => ({ ...sectie, defaultOpen: index === 0 }))
}

export { SECTION_IDS, WONING_BAG_KEYS, INKOMEN_WONEN_KEYS }
export default buildExternalDataSections
